import { useEffect, type RefObject } from "react";

const FONT_LINK_ID = "rl-plus-jakarta-sans";
const FONT_HREF =
  "https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@300;400;500;600;700;800&display=swap";

/** Loads the page font only while a redesigned page is mounted, so no other page picks it up. */
export function useLandingFont() {
  useEffect(() => {
    if (document.getElementById(FONT_LINK_ID)) return;
    const link = document.createElement("link");
    link.id = FONT_LINK_ID;
    link.rel = "stylesheet";
    link.href = FONT_HREF;
    document.head.appendChild(link);
    return () => link.remove();
  }, []);
}

/** Fades sections in as they scroll into view; content stays visible if observers are unavailable. */
export function useReveal(root: RefObject<HTMLElement>) {
  useEffect(() => {
    const el = root.current;
    if (!el || !("IntersectionObserver" in window)) return;
    el.classList.add("rl-js");
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("is-visible");
            io.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.12, rootMargin: "0px 0px -40px 0px" },
    );
    el.querySelectorAll(".rl-reveal").forEach((node) => io.observe(node));
    return () => {
      io.disconnect();
      el.classList.remove("rl-js");
    };
  }, [root]);
}

/**
 * The redesigned pages are written for the app's default <html class="app-theme"> state. That class is stripped
 * when a themed screen (sign-in, dashboard) unmounts, which switches on a global "html:not(.app-theme) *" rule
 * (Raleway, line-height: 1 !important). Re-adding it keeps a revisited page looking the same as a fresh load.
 * It is only ever added here, never removed, so the pages that follow are left in the app's default state.
 */
export function useDefaultAppTheme() {
  useEffect(() => {
    document.documentElement.classList.add("app-theme");
  }, []);
}

/**
 * ReImagine Lite used to remove `app-theme` while open, and the roadmap page it hands over to relies on
 * the class being absent. The redesigned Lite page needs the default theme while it is on screen, then
 * puts the old state back when it unmounts, so the next page sees exactly what it saw before.
 */
export function useAppThemeWhileMounted() {
  useEffect(() => {
    const root = document.documentElement;
    root.classList.add("app-theme");
    return () => root.classList.remove("app-theme");
  }, []);
}
